import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { authFetch, dataFetch } from '../../api';
import { useAuth } from '../../context/AuthContext';
import { ROLES } from '../../data/roles';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import * as FcIcons from 'react-icons/fc';

const { FiMail, FiLock, FiAlertCircle, FiEye, FiEyeOff } = FiIcons;
const { FcGoogle } = FcIcons;

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  // Forced Password Reset States
  const [isForceReset, setIsForceReset] = useState(false);
  const [currentTempPassword, setCurrentTempPassword] = useState('');
  const [dbRecord, setDbRecord] = useState(null);
  const [recordType, setRecordType] = useState(''); // 'employee' or 'admin'

  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const handleNavigation = (mappedUser, isAfterReset = false) => {
    const role = mappedUser.role;
    
    let defaultPath = '/';
    if (role === ROLES.EMPLOYEE || role === 'Learner' || role === 'user') {
      defaultPath = '/employee/dashboard';
    } else if (role === ROLES.MANAGER) {
      defaultPath = '/my-team';
    }

    if (role === ROLES.ADMINISTRATOR || role === ROLES.IT_SECURITY_ADMIN || role === 'Admin') {
      navigate('/', { replace: true });
    } else if (isAfterReset) {
      // Force default route after a mandatory password reset
      navigate(defaultPath, { replace: true });
    } else {
      let fromPath = location.state?.from?.pathname;
      
      // Clear the "from" path if the user explicitly clicked "Logout" recently
      if (sessionStorage.getItem('manual_logout') === 'true') {
        fromPath = null;
        sessionStorage.removeItem('manual_logout');
      }
      
      // Prevent routing non-admins to admin-only pages from stale state
      if (!fromPath || fromPath === '/' || fromPath === '/learners') {
        fromPath = defaultPath;
      }
      
      navigate(fromPath, { replace: true });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (isForceReset) {
      if (password.length < 6) {
        setError('New password must be at least 6 characters long.');
        setLoading(false);
        return;
      }

      // Automatically update the logged-in user's password
      const res = await authFetch('change-password', {
        method: 'POST',
        body: JSON.stringify({ newPassword: password, currentPassword: currentTempPassword })
      });

      if (res?.status === 'success' || !res?.error) {
        // Save credentials for background relogin on 401s
        localStorage.setItem('auth_email', email);
        localStorage.setItem('auth_password', password);

        // Update database record to mark as Active and clear temp_password
        if (recordType === 'employee' && dbRecord) {
          await dataFetch(`update/employees/${dbRecord.id}`, {
            method: 'PUT',
            body: JSON.stringify({ ...dbRecord, inviteStatus: 'Active', temp_password: '' })
          });
        } else if (recordType === 'admin' && dbRecord) {
          await dataFetch(`update/admin_invites/${dbRecord.id}`, {
            method: 'PUT',
            body: JSON.stringify({ ...dbRecord, status: 'Accepted', temp_password: '' })
          });
        }
        
        // Refresh session and navigate
        const sessionRes = await authFetch('get-session');
        if (sessionRes?.data?.user) {
          const mappedUser = await login(sessionRes.data.user);
          handleNavigation(mappedUser, true);
        } else {
          navigate('/');
        }
      } else {
        setError(res?.error || 'Failed to update password. Please try again.');
      }
      setLoading(false);
      return;
    }

    // Normal Sign-in
    const res = await authFetch('sign-in/email', {
      method: 'POST',
      body: JSON.stringify({ email, password })
    });

    if (res?.status === 'success') {
      const userId = res.data.user.id;
      localStorage.setItem('user_id', userId);
      // Save credentials for background relogin on 401s
      localStorage.setItem('auth_email', email);
      localStorage.setItem('auth_password', password);

      // Verify if this user is logging in initially with a temp password
      let needsReset = false;
      let rType = '';
      let record = null;

      // Check employee record
      const empRes = await dataFetch(`read/employees?email=${encodeURIComponent(email)}`);
      if (empRes?.data?.length > 0 && empRes.data[0].inviteStatus === 'Invited') {
        needsReset = true;
        rType = 'employee';
        record = empRes.data[0];
      } else {
        // Check admin invite record
        const adminRes = await dataFetch(`read/admin_invites?email=${encodeURIComponent(email)}`);
        if (adminRes?.data?.length > 0 && adminRes.data[0].status === 'Pending') {
          needsReset = true;
          rType = 'admin';
          record = adminRes.data[0];
        }
      }

      if (needsReset) {
        setIsForceReset(true);
        setCurrentTempPassword(password); // Save the temp password to authorize the change
        setDbRecord(record);
        setRecordType(rType);
        setPassword(''); // Clear the password field so they can type the new one
        setLoading(false);
        return;
      }

      const mappedUser = await login(res.data.user);
      handleNavigation(mappedUser, false);
    } else {
      setError(res?.error || 'Invalid email or password');
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    const callbackURL = encodeURIComponent(`${window.location.origin}${window.location.pathname}#/auth/callback`);
    window.location.href = `https://rmvzorxcl35mttidiexhtp5g2m0hpsqo.lambda-url.us-east-2.on.aws/auth/sign-in/social?provider=google&callbackURL=${callbackURL}&Instance=48568_bis_db`;
  };

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
        <div className="p-8">
          <div className="flex items-center justify-center gap-2 mb-8">
            <div className="w-10 h-10 bg-slate-900 rounded-xl flex items-center justify-center">
              <SafeIcon icon={FiIcons.FiShield} className="text-[#00E5FF] text-xl" />
            </div>
            <span className="text-2xl font-bold text-slate-800 tracking-tight">Sentinel</span>
          </div>

          {isForceReset ? (
            <>
              <h2 className="text-2xl font-bold text-slate-800 mb-2 text-center">Set New Password</h2>
              <p className="text-slate-500 mb-8 text-center text-sm">Please choose a permanent password to secure your account.</p>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-slate-800 mb-2 text-center">Welcome back</h2>
              <p className="text-slate-500 mb-8 text-center text-sm">Sign in to your account to continue</p>
            </>
          )}

          {error && (
            <div className="mb-6 p-3 bg-red-50 border border-red-100 rounded-lg flex items-start gap-3">
              <SafeIcon icon={FiAlertCircle} className="text-red-500 mt-0.5 flex-shrink-0" />
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            
            {!isForceReset && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
                <div className="relative">
                  <SafeIcon icon={FiMail} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:bg-white transition-all text-sm"
                    placeholder="name@company.com"
                    required
                    disabled={isForceReset}
                  />
                </div>
              </div>
            )}

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-sm font-medium text-slate-700">
                  {isForceReset ? 'New Password' : 'Password'}
                </label>
                {!isForceReset && (
                  <Link to="/forgot-password" className="text-xs text-blue-600 hover:text-blue-700 font-medium">
                    Forgot password?
                  </Link>
                )}
              </div>
              <div className="relative">
                <SafeIcon icon={FiLock} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-10 pr-10 py-2.5 bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-slate-900 focus:bg-white transition-all text-sm"
                  placeholder={isForceReset ? "At least 6 characters" : "••••••••"}
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 focus:outline-none flex items-center justify-center"
                >
                  <SafeIcon icon={showPassword ? FiEyeOff : FiEye} />
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-slate-900 text-white py-2.5 rounded-lg text-sm font-medium hover:bg-slate-800 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:ring-offset-2 transition-all disabled:opacity-50 mt-6"
            >
              {loading ? (isForceReset ? 'Updating...' : 'Signing in...') : (isForceReset ? 'Update Password & Continue' : 'Sign in')}
            </button>
            
            <div className="mt-6 text-center">
              <Link to="/privacy-policy" className="text-xs text-slate-500 hover:text-slate-800 transition-colors">
                Privacy Policy
              </Link>
            </div>
          </form>

        </div>
      </div>
    </div>
  );
};

export default Login;